import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fetch (once) the classification flags for every employee — used to show
 * "مرن / أجير / طبيب راتب / طبيب نسبة" badges next to names across the admin
 * app. Cached in memory at module scope so navigating between pages doesn't
 * re-issue the request.
 *
 * Returns:
 *   { badges:  { [user_id]: {is_doctor, doctor_type, is_contracted_worker,
 *                            employment_type, manual_attendance_allowed} },
 *     loading: bool,
 *     refresh: () => Promise<void> }
 */
let _cache = null;
let _inflight = null;

export const useEmployeeBadges = () => {
  const [badges, setBadges] = useState(_cache || {});
  const [loading, setLoading] = useState(!_cache);

  const load = useCallback(async () => {
    if (_inflight) return _inflight;
    _inflight = axios
      .get(`${API}/employees/badges`, { withCredentials: true })
      .then(({ data }) => {
        _cache = data || {};
        setBadges(_cache);
      })
      .catch(() => {
        _cache = _cache || {};
        setBadges(_cache);
      })
      .finally(() => {
        _inflight = null;
        setLoading(false);
      });
    return _inflight;
  }, []);

  useEffect(() => {
    if (!_cache) load();
  }, [load]);

  return {
    badges,
    loading,
    refresh: async () => { _cache = null; await load(); },
    /** Helper: return the badge-data shape for a specific user id. */
    getBadge: (userId) => (userId && badges[userId]) || null,
  };
};

export default useEmployeeBadges;
